'use client';

import { useEffect, useState } from 'react';
import type { NetProceedsBlockProps } from '../types';
import { theme, sectionColors, buttonVars } from '../theme';
import { CALC_SMS_CONSENT_TEXT, FIELD, NumberField, PRIMARY_BTN, PdfGate, money, numberOrUndefined, pageUrl, readBrowserId, readValuationToken } from './calcShared';

/**
 * Net-proceeds calculator — V3.1 seller calculators (docs/V3_LAUNCH_PLAN.md §4). CLIENT block,
 * framework-agnostic:
 *
 *   1. inputs (sale price, state, commission %, payoff, prep budget, other fees)
 *      → POST same-origin /api/calc/net-proceeds; the gateway itemizes closing costs from its
 *      state table and records `proceeds_calculated` when the browser is known.
 *   2. results: every line, total selling costs, payoff, the net — re-run on any change.
 *   3. the gate ("Get the itemized breakdown (PDF)"): name + email (+ phone, unchecked SMS consent)
 *      → POST /api/calc/unlock (PdfGate) → seller lead, contact attributes, drip, PDF by email.
 *
 * `?valuation=<token>` on the page (the value report's "What if I sold today?" link and the
 * valuation drip) runs once on mount with the visitor's AVM mid as the sale price.
 */

interface Line { key: string; label: string; amount: number; note?: string }
interface Outputs { table: string; salePrice: number; commissionPct: number; lines: Line[]; totalSellingCosts: number; payoff: number; netProceeds: number; netPct: number }
interface RunResponse {
  data?: { run_id: string | null; inputs: Record<string, unknown>; outputs: Outputs; prefill: { sale_price: number; state: string | null; address: string } | null } | null;
  error?: string | null;
}

interface Inputs { sale_price?: number; state?: string; commission_pct?: number; payoff?: number; prep_budget?: number; other_fees?: number }

export function NetProceedsBlock(props: NetProceedsBlockProps & { turnstileSiteKey?: string | null }) {
  const {
    heading = 'What would you walk away with?',
    subheading = 'Enter a sale price and your mortgage payoff — we itemize the typical selling costs for your state.',
    buttonText = 'Calculate my net',
    defaultState = '',
    defaultCommissionPct = 5,
    showPhone = true,
    phoneRequired = false,
    consentText = CALC_SMS_CONSENT_TEXT,
  } = props;
  // The section's button colours ride on the section as --blk-btn vars (plan E6).
  const colors = { ...sectionColors(props, { bg: 'transparent', text: theme.text }), ...buttonVars(props) };

  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ runId: string | null; outputs: Outputs; address: string | null } | null>(null);
  const [prefill, setPrefill] = useState<{ sale_price?: number; state?: string }>({});

  async function run(inputs: Inputs, valuationToken?: string) {
    setError(null);
    setPending(true);
    try {
      const res = await fetch('/api/calc/net-proceeds', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...inputs, valuation_token: valuationToken, browser_id: readBrowserId(), page_url: pageUrl() }),
      });
      const body = (await res.json().catch(() => ({}))) as RunResponse;
      if (!res.ok || !body.data) throw new Error(body.error || `Request failed: ${res.status}`);
      setResult({ runId: body.data.run_id, outputs: body.data.outputs, address: body.data.prefill?.address ?? null });
      if (body.data.prefill) setPrefill({ sale_price: body.data.prefill.sale_price, state: body.data.prefill.state ?? undefined });
    } catch (err) {
      setError(err instanceof Error && err.message && !/Request failed/.test(err.message) ? err.message : 'Sorry, we could not run the numbers. Please check the inputs and try again.');
    } finally {
      setPending(false);
    }
  }

  // The value report's "sold today" link: run once with the AVM mid, then let the visitor edit.
  useEffect(() => {
    const token = readValuationToken();
    if (token) void run({ state: defaultState || undefined, commission_pct: defaultCommissionPct }, token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (pending) return;
    const fd = new FormData(e.currentTarget);
    const salePrice = numberOrUndefined(fd.get('sale_price'));
    if (!salePrice || salePrice <= 0) { setError('Please enter the sale price you have in mind.'); return; }
    void run({
      sale_price: salePrice,
      state: String(fd.get('state') ?? '').trim().toUpperCase() || undefined,
      commission_pct: numberOrUndefined(fd.get('commission_pct')),
      payoff: numberOrUndefined(fd.get('payoff')),
      prep_budget: numberOrUndefined(fd.get('prep_budget')),
      other_fees: numberOrUndefined(fd.get('other_fees')),
    });
  }

  const out = result?.outputs ?? null;

  return (
    <section id="net-proceeds" className="px-6 py-12" style={{ ...colors, fontFamily: theme.bodyFont }}>
      <div className="mx-auto max-w-3xl">
        <h2 className="mb-2 text-2xl font-semibold" style={{ fontFamily: theme.headingFont }}>{heading}</h2>
        {subheading ? <p className="mb-6 opacity-80">{subheading}</p> : null}
        {result?.address ? <p className="mb-4 text-sm opacity-70">Starting from your estimate for {result.address}.</p> : null}
        {error ? <p className="mb-4 rounded-md bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">{error}</p> : null}

        <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
          <form onSubmit={handleSubmit} className="space-y-4" data-calc="net-proceeds-form" key={`${prefill.sale_price ?? ''}-${prefill.state ?? ''}`}>
            <NumberField name="sale_price" label="Expected sale price" prefix="$" step={1000} defaultValue={prefill.sale_price} placeholder="600,000" required />
            <label htmlFor="calc-state" className="block text-sm">
              <span className="mb-1 block font-medium">State</span>
              <input id="calc-state" name="state" type="text" maxLength={2} placeholder="FL" defaultValue={prefill.state ?? defaultState} className={`${FIELD} uppercase`} autoComplete="address-level1" />
              <span className="mt-1 block text-xs opacity-70">Sets the transfer-tax, title and attorney lines. Blank uses a typical national table.</span>
            </label>
            <NumberField name="commission_pct" label="Total commission" suffix="%" step={0.25} defaultValue={defaultCommissionPct} help="Both sides combined; negotiable." />
            <NumberField name="payoff" label="Mortgage payoff" prefix="$" step={1000} placeholder="0" help="Every loan or lien on the home." />
            <NumberField name="prep_budget" label="Repairs & prep before listing" prefix="$" step={500} placeholder="0" />
            <NumberField name="other_fees" label="Other fees or concessions" prefix="$" step={100} placeholder="0" />
            <button type="submit" disabled={pending} className={`blk-btn ${PRIMARY_BTN}`}>
              {pending ? 'Running the numbers…' : out ? 'Update my numbers' : buttonText}
            </button>
          </form>

          <div>
            {out ? (
              <div className="rounded-lg border border-black/10 p-5" data-calc="net-proceeds-result">
                <p className="text-xs uppercase tracking-wide opacity-70">Estimated net proceeds</p>
                <p className="mt-1 text-3xl font-semibold" style={{ fontFamily: theme.headingFont }}>{money(out.netProceeds)}</p>
                <p className="mt-1 text-sm opacity-70">{out.netPct}% of a {money(out.salePrice)} sale</p>
                <table className="mt-4 w-full text-sm">
                  <tbody>
                    {out.lines.map((l) => (
                      <tr key={l.key} className="border-t border-black/10">
                        <td className="py-2 pr-3">
                          {l.label}
                          {l.note ? <span className="block text-xs opacity-60">{l.note}</span> : null}
                        </td>
                        <td className="py-2 text-right whitespace-nowrap">− {money(l.amount)}</td>
                      </tr>
                    ))}
                    <tr className="border-t border-black/20 font-medium">
                      <td className="py-2 pr-3">Total selling costs</td>
                      <td className="py-2 text-right whitespace-nowrap">− {money(out.totalSellingCosts)}</td>
                    </tr>
                    <tr className="border-t border-black/10">
                      <td className="py-2 pr-3">Mortgage payoff</td>
                      <td className="py-2 text-right whitespace-nowrap">− {money(out.payoff)}</td>
                    </tr>
                    <tr className="border-t border-black/20 text-base font-semibold">
                      <td className="py-2 pr-3">Net proceeds</td>
                      <td className="py-2 text-right whitespace-nowrap">{money(out.netProceeds)}</td>
                    </tr>
                  </tbody>
                </table>
                <p className="mt-3 text-xs opacity-60">
                  An estimate built from typical {out.table === 'DEFAULT' ? 'national' : out.table} closing costs and the numbers you entered — not an appraisal or a net sheet.
                </p>
                {result?.runId ? (
                  <PdfGate kind="net_proceeds" runId={result.runId} showPhone={showPhone} phoneRequired={phoneRequired} consentText={consentText} turnstileSiteKey={props.turnstileSiteKey} />
                ) : null}
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-black/15 p-5 text-sm opacity-70" data-calc="net-proceeds-empty">
                Your itemized estimate — commission, transfer taxes, title, recording, prep — appears here.
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
